import { useUserLocation } from 'Account/hooks';
import { useCoachingAndTherapyUserSessionLimits, useMatches } from 'Book';
import { useSectionErrorLogging } from './useSectionErrorLogging';

export enum PractitionerBookingStep {
  Match = 'match',
  Book = 'book',
}

type PractitionerBookingOptions =
  | { isLoading: boolean; step: null; remaining: null }
  | { isLoading: false; step: PractitionerBookingStep; remaining: number };

export const usePractitionerBookingOptions = ({
  skipMatches,
}: {
  skipMatches: boolean;
}): PractitionerBookingOptions => {
  const {
    userLocationData,
    hasValidLocationData,
    loading: locationLoading,
    error: locationError,
  } = useUserLocation();
  useSectionErrorLogging(locationError, userLocationData, 'todo-list');

  const {
    userHasPractitionerMatches,
    aiMatchingEnabled,
    practitionerMatchesLoading: matchesLoading,
    practitionerMatchesError: matchesError,
  } = useMatches({
    skip: skipMatches || locationLoading || !hasValidLocationData,
  });
  useSectionErrorLogging(matchesError, userHasPractitionerMatches, 'todo-list');

  const {
    remainingSessions: remaining,
    sessionsExpired,
    loading: allowanceLoading,
    error: allowanceError,
  } = useCoachingAndTherapyUserSessionLimits();
  useSectionErrorLogging(allowanceError, remaining !== undefined, 'todo-list');

  const isLoading = allowanceLoading || matchesLoading || locationLoading;
  const isError = Boolean(allowanceError || locationError || matchesError);

  if (
    isLoading ||
    isError ||
    remaining === undefined ||
    remaining <= 0 ||
    sessionsExpired
  ) {
    return { isLoading, step: null, remaining: null };
  }

  return {
    isLoading: false,
    step:
      aiMatchingEnabled && !userHasPractitionerMatches
        ? PractitionerBookingStep.Match
        : PractitionerBookingStep.Book,
    remaining,
  };
};
